import React, { createContext, useContext, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import type { PermissionType } from '@/hooks/useRoleManagement';
import type { Workspace } from '@/types/workspace';
import { useModuleAccess } from '@/hooks/useModuleAccess';

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  isLoading: boolean;
  error: Error | null;
  contextStatus: 'loading' | 'ready' | 'error';
  isOwner: boolean;
  hasPermission: (resource: string, permissionType?: PermissionType) => Promise<boolean>;
  hasPermissionSync: (resource: string) => boolean;
  hasModule: (moduleId: string) => boolean;
  switchWorkspace: (workspaceId: string) => void;
  brand: any;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export { WorkspaceContext };

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { permissions: userPermissions } = usePermissions();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Enhanced debug logging for provider nesting fix
  console.group('=== WORKSPACE CTX ===');
  console.log('pathname:', location.pathname);
  console.log('param.workspaceId:', workspaceId);
  console.log('user.id:', user?.id || 'undefined');
  console.log('user.email:', user?.email || 'undefined');
  console.log('query enabled:', Boolean(user?.id && workspaceId));
  console.groupEnd();

  const queryEnabled = Boolean(user?.id && workspaceId);

  // CRITICAL: Only the main workspace query affects contextStatus
  const { 
    data: workspace, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['workspace', workspaceId, user?.id],
    enabled: queryEnabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspaces')
        .select(`
          id, name, slug, owner_id, plan_id, enabled_modules, is_default, created_at, updated_at,
          organization:organizations(id, name, type, primary_contact, status)
        `)
        .eq('id', workspaceId)
        .single();
      
      if (error) throw error;
      
      console.log('[CTX] loaded workspace:', { id: data.id, name: data.name, plan_id: data.plan_id });
      return data as Workspace;
    },
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  // Check if user is owner (server-side check)
  const { data: isOwnerResult } = useQuery({
    queryKey: ['is-owner', user?.email],
    queryFn: async () => {
      if (!user?.email) return false;
      const { data, error } = await supabase.rpc('is_owner');
      if (error) {
        console.error('[CTX] Owner check error:', error);
        return false;
      }
      console.log('[CTX] Owner check result for', user.email, ':', data);
      return data || false;
    },
    enabled: !!user?.email,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });

  // Use unified module access hook
  const moduleAccess = useModuleAccess('workspace', workspaceId || '');

  const contextStatus = useMemo((): 'loading' | 'ready' | 'error' => {
    console.log('=== WORKSPACE CONTEXT STATUS DEBUG ===');
    console.log('workspaceId:', workspaceId);
    console.log('user:', !!user);
    console.log('isLoading:', isLoading);
    console.log('error:', error);
    console.log('workspace:', !!workspace);
    console.log('query enabled:', queryEnabled);
    console.log('========================================');

    // Context status depends ONLY on the workspace query
    if (!queryEnabled) return 'loading';
    if (isLoading) return 'loading';
    if (error) return 'error';
    if (!workspace) return 'loading';
    return 'ready';
  }, [queryEnabled, isLoading, error, workspace]);

  // Analytics queries - wrapped to NOT affect contextStatus
  useQuery({
    queryKey: ['workspace-analytics', workspaceId],
    queryFn: async () => {
      if (!workspace) return null;
      
      try {
        // This might fail with 403 - that's OK, we ignore it
        const { data } = await supabase
          .from('ingestion_events')
          .select('*')
          .eq('user_id', user?.id)
          .limit(10);
        
        return data;
      } catch (error) {
        console.warn('[Analytics] Non-critical error (ignored):', error);
        return null;
      }
    },
    enabled: contextStatus === 'ready',
    retry: 0,
  });

  // Owner detection with fallback - prioritize moduleAccess result
  const isOwner = useMemo(() => {
    // Primary: entitlements-based owner detection
    if (moduleAccess.isOwner) return true;
    
    // Secondary: server-side is_owner function result
    if (isOwnerResult === true) return true;
    
    // Fallback: email-based check
    if (user?.email === 'hari@billportal.io') return true;
    
    // Fallback: workspace owner_id check
    if (workspace?.owner_id === user?.id) return true;
    
    // Fallback: permissions system
    if (userPermissions?.isOwner) return true;
    
    return false;
  }, [moduleAccess.isOwner, isOwnerResult, user?.email, user?.id, workspace?.owner_id, userPermissions?.isOwner]);

  const hasPermission = async (resource: string, permissionType: PermissionType = 'read'): Promise<boolean> => {
    // Owner/Super Admin bypass
    if (isOwner || userPermissions?.hasFullAccess) return true;
    
    return userPermissions?.checkPermission(resource) || false;
  };

  const hasPermissionSync = (resource: string): boolean => {
    // Owner/Super Admin bypass  
    if (isOwner || userPermissions?.hasFullAccess) return true;
    
    // For non-admin users, show basic access for dashboard/bills
    if (resource === 'dashboard' || resource === 'bills') return true;
    
    return false;
  };

  // Server-side module check with owner bypass using unified entitlements
  const hasModule = (moduleId: string): boolean => {
    console.log('[CTX] hasModule check:', { moduleId, isOwner: moduleAccess.isOwner });
    
    // Use the unified module access hook
    return moduleAccess.hasModule(moduleId);
  };

  const switchWorkspace = (newWorkspaceId: string) => {
    const currentPath = location.pathname;
    const pathWithoutWorkspace = currentPath.replace(/\/w\/[^\/]+/, '');
    navigate(`/w/${newWorkspaceId}${pathWithoutWorkspace || '/dashboard'}`);
    
    // Force refresh to clear state
    setTimeout(() => {
      queryClient.invalidateQueries();
    }, 100);
  };

  const value: WorkspaceContextType = {
    currentWorkspace: workspace || null,
    isLoading,
    error: error as Error | null,
    contextStatus,
    isOwner,
    hasPermission,
    hasPermissionSync,
    hasModule,
    switchWorkspace,
    brand: {},
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = (): WorkspaceContextType | null => {
  const context = useContext(WorkspaceContext);
  return context || null;
};